<template>
  <div class="select-courses">
    <h2 class="title">
      Select Courses
      <VideoHoverPlayer title="Selecting Courses / Taking a Course" videoUrl="https://player.vimeo.com/video/1102228058" />
    </h2>
    <div class="step-badge">{{ stepText }}</div>
    <div class="courses-wrapper">
      <!-- 🔍 Search -->
      <div class="search-container">
        <input
          type="text"
          class="search-input"
          placeholder="Search Courses"
          v-model="searchQuery"
        />
        <span class="search-icon">
          <img src="@/assets/icons/search-icon.svg" alt="Search Icon" />
        </span>
      </div>

      <!-- 🧩 Courses Grid -->
       <div class="course-section">
      <div class="courses-grid ">
        <!-- Skeleton Loading -->
        <template v-if="loading">
          <div class="courses-column">
            <div
              v-for="n in 8"
              :key="'skeleton-' + n"
              class="course-item skeleton-item"
            >
              <div class="skeleton-checkbox"></div>
              <div class="skeleton-text"></div>
            </div>
          </div>
          <div class="courses-column">
            <div
              v-for="n in 8"
              :key="'skeleton-second-' + n"
              class="course-item skeleton-item"
            >
              <div class="skeleton-checkbox"></div>
              <div class="skeleton-text"></div>
            </div>
          </div>
        </template>
        <!-- Actual Courses -->
        <template v-else>
          <!-- Included (free/plan) courses -->
          <div class="courses-column">
            <div
              v-for="(course, index) in filteredFirstHalf"
              :key="'included-' + index"
              class="course-item"
            >
              <input
                type="checkbox"
                :id="'included-' + index"
                :checked="isCourseSelected(course)"
                @change="toggleCourse(course)"
              />
              <label :for="'included-' + index">{{ course.name }}</label>
            </div>
          </div>

          <!-- Additional (special) courses -->
          <div class="courses-column">
            <div
              v-for="(course, index) in filteredSecondHalf"
              :key="'additional-' + index"
              class="course-item"
            >
              <input
                type="checkbox"
                :id="'additional-' + index"
                :checked="isCourseSelected(course)"
                @change="toggleCourse(course)"
              />
              <label :for="'additional-' + index">{{ course.name }}</label>
            </div>
          </div>
        </template>
      </div>
      </div>
    </div>

    <!-- 🧮 User count modal -->
    <modal :show.sync="showUserModal">
      <h4
        slot="header"
        class="title title-up text-center"
        style="color: #444c57"
      >
        {{ specialCourseName }}
      </h4>
      <div class="col-md-8 mx-auto">
        <base-input
          label="How many users will be taking this course:"
          type="number"
          min="1"
          :key="specialCourseName"
          v-model.number="users"
        />
      </div>
      <div class="col-md-6 mx-auto mt-3 text-center">
        <base-button
          size="md"
          class="custom-btn"
          style="background-color: #e66412; color: white; border-color: #e66412"
          @click="confirmSpecialCourse"
        >
          Done
        </base-button>
      </div>
    </modal>
  </div>
</template>

<script>
import VideoHoverPlayer from "@/components/Signup/VideoHoverPlayer.vue";

export default {
  name: "SelectCourses",
  components: { VideoHoverPlayer },
  props: {
    choosePlatform: {
      type: String,
      required: false,
    },
    accountType: {
      type: String,
      required: false,
    },
    courses: {
      type: Array,
      default: () => [],
    },
    tempSelectedCourses: { required: true },
    tempSelectedSpecialCourses: { required: true },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchQuery: "",
      selectedCourses: this.tempSelectedCourses ?? [],
      selectedSpecialCourses: this.tempSelectedSpecialCourses ?? [],
      showUserModal: false,
      specialCourseName: "",
      specialCourseId: null,
      users: 1,
    };
  },
  computed: {
    stepText() {
      return this.choosePlatform === "chooseOtherPlatform"
        ? "Step 4/5"
        : "Step 2/3";
    } ,
    filteredCourses() {
      return this.courses.filter((course) =>
        course.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    },

    firstHalf() {
      const mid = Math.ceil(this.filteredCourses.length / 2);
      return this.filteredCourses.slice(0, mid);
    },
    secondHalf() {
      const mid = Math.ceil(this.filteredCourses.length / 2);
      return this.filteredCourses.slice(mid);
    },

    filteredFirstHalf() {
      return this.firstHalf;
    },
    filteredSecondHalf() {
      return this.secondHalf;
    },
  },
  methods: {
    isCourseSelected(course) {
      return (
        this.selectedCourses.includes(course) ||
        this.selectedSpecialCourses.some((c) => c.id === course.id)
      );
    },

    toggleCourse(course) {
      const isSelected = this.isCourseSelected(course);

      if (isSelected) {
        // Remove from both lists
        this.selectedCourses = this.selectedCourses.filter(
          (c) => c.id !== course.id
        );
        this.selectedSpecialCourses = this.selectedSpecialCourses.filter(
          (c) => c.id !== course.id
        );
        this.emitCourses();
        return;
      }

      // Special course (requires users)
      if (course.is_discounted_course === 0 && this.accountType != 'individual') {
        this.specialCourseName = course.name;
        this.specialCourseId = course.id;
        this.showUserModal = true;
      } else {
        // Normal course
        this.selectedCourses.push(course);
        this.emitCourses();
      }
    },

    confirmSpecialCourse() {
      const course = this.courses.find(
        (c) => c.id === this.specialCourseId
      );
      if (!course) return;

      // Add as special
      this.selectedSpecialCourses.push({
        id: course.id,
        name: course.name,
        users: this.users,
      });

      this.emitCourses();
      this.resetModal();
    },

    resetModal() {
      this.showUserModal = false;
      this.specialCourseName = "";
      this.specialCourseId = null;
      this.users = 1;
    },

    emitCourses() {
      this.$emit("courses-selected", {
        selectedCourses: this.selectedCourses,
        selectedSpecialCourses: this.selectedSpecialCourses,
      });
    },
  },

};
</script>

<style lang="scss" scoped>
.course-section {
  background-color: #fff;
  border: 1px solid rgba(235, 26, 38, 0.2);
  border-radius: 15px;
  padding: 20px;
  height: 471px;
  // overflow-y: scroll;
  display: flex;
  flex-direction: column;
}
.select-courses {
  position: relative;
  margin-top: 30px;
  margin-bottom: 30px;
}
.courses-wrapper {
  border: 1px solid rgba(235, 26, 38, 0.2);
  padding: 20px;
  border-radius: 15px;
}

.title {
  font-size: 20px;
  line-height: 25px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #252525;
  display: flex;
  align-items: center;
  .play-icon {
    color: #eb1a26;
    margin-left: 10px;
    font-size: 24px;
    display: none;
  }
}

.step-badge {
  position: absolute;
  right: 0;
  top: -7px;
  background: #fff0ea;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 10px;
  line-height: 23px;
  font-weight: 700;
  color: #252525;
}

.search-container {
  position: relative;
  margin-bottom: 20px;
}

.search-input {
  width: 100%;
  padding: 12px 20px;
  padding-left: 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.courses-grid {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.courses-column {
  flex: 1;
}

.course-item {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.course-item input[type="checkbox"] {
  display: none;
}

.course-item label {
  font-size: 16px;
  cursor: pointer;
  margin-bottom: 0;
  color: #252525;
  position: relative;
  padding-left: 26px;
  padding-bottom: 0px !important;

  &:before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    // transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border: 1px solid rgba(200, 200, 200, 0.6);
    border-radius: 4px;
    background-color: white;
  }
}

.course-item input[type="checkbox"]:checked + label {
  &:before {
    background-image: url("~@/assets/icons/checked-icon.svg");
    background-position: center;
    background-repeat: no-repeat;
  }
}

// Skeleton Loading Styles
.skeleton-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.skeleton-checkbox {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  flex-shrink: 0;
}

.skeleton-text {
  height: 16px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s ease-in-out infinite;
  flex: 1;
  max-width: 200px;
  
  @media (max-width: 768px) {
    max-width: 150px;
  }
}

@keyframes skeleton-loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

@media (min-width: 768px) {
  .select-courses {
    margin-top: 60px;
  }
  .title {
    font-size: 32px;
    line-height: 40px;
    font-weight: 700;
    margin-bottom: 20px;
    .play-icon {
      color: #eb1a26;
      margin-left: 10px;
      font-size: 24px;
      display: block;
    }
  }

  .step-badge {
    position: absolute;
    right: 0;
    top: 0;
    background: #fff0ea;
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 18px;
    line-height: 23px;
    font-weight: 700;
    color: #252525;
  }

  .courses-grid {
    flex-direction: row;
    gap: 30px;
  }
}
</style>
